import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    Grower,
    ListGrowersParams,
    UpsertGrowerRequest,
} from '@/lib/api/growers-api'
import { growersApi } from '@/lib/api/growers-api'
import { m } from '@/lib/paraglide/messages'

const INFINITE_PAGE_SIZE = 20

export const growerKeys = {
    all: ['growers'] as const,
    lists: () => [...growerKeys.all, 'list'] as const,
    list: (params: ListGrowersParams) =>
        [...growerKeys.lists(), params] as const,
    detail: (id: string) => [...growerKeys.all, 'detail', id] as const,
    infinite: (search: string) =>
        [...growerKeys.lists(), 'infinite', search] as const,
    areaLink: (areaId: string) =>
        [...growerKeys.all, 'area-link', areaId] as const,
}

// useGrowerAreaGrowerLink: màn chi tiết hộ tài khoản được mở từ trang QR vùng
// trồng. Server 404 = "không được xem hộ này", không phải sự cố → không retry.
// `null` = không hỏi (khách, không có quyền xem hộ nào).
export function useGrowerAreaGrowerLink(areaId: string | null) {
    return useQuery({
        queryKey: growerKeys.areaLink(areaId ?? ''),
        queryFn: async () => {
            const res = await growersApi.areaGrowerLink(areaId as string)
            return res.data ?? null
        },
        enabled: !!areaId,
        retry: false,
    })
}

interface InfiniteGrowerPage {
    items: Grower[]
    meta:
        | {
              page: number
              per_page: number
              total: number
              total_pages: number
          }
        | undefined
}

/**
 * Paginated grower feed for the picker: the server filters by name, the client
 * only appends pages.
 *
 * Loading every grower up front is what this replaces — the old callers took the
 * default page size and silently showed the first 20 (or 100) households by name,
 * making the rest unselectable. Search must therefore go to the server: filtering
 * an already-loaded page would reproduce exactly that bug.
 *
 * Soft-deleted households are excluded by the API and stay excluded here.
 */
export function useInfiniteGrowersQuery(search: string) {
    return useInfiniteQuery({
        queryKey: growerKeys.infinite(search),
        queryFn: async ({ pageParam = 1 }): Promise<InfiniteGrowerPage> => {
            const res = await growersApi.list({
                page: pageParam,
                per_page: INFINITE_PAGE_SIZE,
                search: search || undefined,
            })
            return { items: res.data ?? [], meta: res.meta }
        },
        initialPageParam: 1,
        // Mỗi từ khoá là một queryKey mới. Không giữ dữ liệu cũ thì trong lúc
        // chờ response, options rỗng và cmdk render CommandEmpty — người dùng
        // thấy "Không tìm thấy hộ trồng" chớp lên ở mỗi lần gõ dù có kết quả.
        placeholderData: (prev) => prev,
        getNextPageParam: (lastPage) => {
            // No meta means the server did not paginate this response; stopping is
            // the only safe read — guessing another page would loop forever.
            if (!lastPage.meta) return undefined
            return lastPage.meta.page < lastPage.meta.total_pages
                ? lastPage.meta.page + 1
                : undefined
        },
    })
}

export function useGrowers(params?: ListGrowersParams) {
    return useQuery({
        queryKey: growerKeys.list(params ?? {}),
        queryFn: async () => {
            const res = await growersApi.list(params)
            return { data: res.data ?? [], meta: res.meta }
        },
    })
}

export function useGrower(id: string | null | undefined) {
    return useQuery({
        queryKey: growerKeys.detail(id ?? ''),
        queryFn: async () => {
            const res = await growersApi.getById(id as string)
            return res.data
        },
        enabled: !!id,
    })
}

export function useCreateGrower() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: UpsertGrowerRequest) => growersApi.create(data),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: growerKeys.lists() })
            toast.success(m.toast_grower_save_success())
        },
        onError: () => toast.error(m.toast_grower_save_error()),
    })
}

export function useUpdateGrower(id: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: UpsertGrowerRequest) => growersApi.update(id, data),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: growerKeys.lists() })
            void qc.invalidateQueries({ queryKey: growerKeys.detail(id) })
            toast.success(m.toast_grower_save_success())
        },
        onError: () => toast.error(m.toast_grower_save_error()),
    })
}

export function useDeleteGrower() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => growersApi.delete(id),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: growerKeys.lists() })
            toast.success(m.toast_grower_delete_success())
        },
        onError: () => toast.error(m.toast_grower_delete_error()),
    })
}

export function useAttachGrowerFile(growerId: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (fileId: string) => growersApi.attachFile(growerId, fileId),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: growerKeys.detail(growerId) })
            toast.success(m.toast_grower_file_attach_success())
        },
        onError: () => toast.error(m.toast_grower_file_attach_error()),
    })
}

export function useDetachGrowerFile(growerId: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (fileId: string) => growersApi.detachFile(growerId, fileId),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: growerKeys.detail(growerId) })
            toast.success(m.toast_grower_file_detach_success())
        },
        onError: () => toast.error(m.toast_grower_file_detach_error()),
    })
}
